<script setup>
import { computed, ref } from "vue";
import { toSafeString } from "@/utils/strings";
import { mobileRouteStore } from "@/_store/mobileRouteStore";
import DayRouteView from "../../routes-mobile/features/day-route/DayRouteView.vue";
import ServiceDetailView from "../../routes-mobile/features/service-detail/ServiceDetailView.vue";
import ScanPackagesView from "../../routes-mobile/features/scan-packages/ScanPackagesView.vue";
import FinishFormView from "../../routes-mobile/features/finish-form/FinishFormView.vue";
import FinishLoadUnloadView from "../../routes-mobile/features/finish-load-unload/FinishLoadUnloadView.vue";

const props = defineProps({
  planId: {
    type: String,
    default: "",
  },
  planName: {
    type: String,
    default: "",
  },
  vehicleId: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["completed"]);
const mobileRoutesStore = mobileRouteStore();

const isOpen = ref(false);
const flowStack = ref([]);
const flowContext = ref({
  planId: "",
  vehicleId: "",
  routeStopId: "",
  stopId: "",
  completionMode: "",
  planName: "",
});

const viewMap = Object.freeze({
  "day-route": DayRouteView,
  "service-detail": ServiceDetailView,
  "scan-packages": ScanPackagesView,
  "finish-form": FinishFormView,
  "finish-load-unload": FinishLoadUnloadView,
});

const currentView = computed(() => {
  const top = flowStack.value[flowStack.value.length - 1] || "";
  return viewMap[top] || null;
});


function close() {
  isOpen.value = false;
  flowStack.value = [];
  flowContext.value = {
    planId: "",
    vehicleId: "",
    routeStopId: "",
    stopId: "",
    completionMode: "",
    planName: "",
  };
}

function open(payload = {}) {
  const planId = toSafeString(payload?.planId || props.planId);
  if (!planId) {
    return;
  }

  flowContext.value = {
    ...flowContext.value,
    planId: planId || toSafeString(props.planId),
    vehicleId: toSafeString(payload?.vehicleId || props.vehicleId),
    routeStopId: toSafeString(payload?.routeStopId),
    stopId: toSafeString(payload?.stopId),
    completionMode: toSafeString(payload?.completionMode || payload?.mode),
    planName: toSafeString(payload?.planName || props.planName),
  };
  flowStack.value = ["day-route"];
  isOpen.value = true;

  const hasIncidentEvents =
    Array.isArray(mobileRoutesStore.incidentEvents) &&
    mobileRoutesStore.incidentEvents.length > 0;

  if (!hasIncidentEvents) {
    mobileRoutesStore.loadIncidentEvents();
  }
}

function navigate(view, payload = {}) {
  if (!viewMap[view]) {
    return;
  }

  flowContext.value = {
    ...flowContext.value,
    ...payload,
    planId: toSafeString(payload?.planId || flowContext.value.planId || props.planId),
    vehicleId: toSafeString(payload?.vehicleId || flowContext.value.vehicleId || props.vehicleId),
    routeStopId: toSafeString(
      payload?.routeStopId || payload?.stopId || flowContext.value.routeStopId
    ),
    stopId: toSafeString(
      payload?.stopId || payload?.routeStopId || flowContext.value.stopId
    ),
    completionMode: toSafeString(
      payload?.completionMode || payload?.mode || flowContext.value.completionMode
    ),
    planName: toSafeString(payload?.planName || flowContext.value.planName || props.planName),
  };
  flowStack.value = [...flowStack.value, view];
}

function back() {
  if (flowStack.value.length <= 1) {
    close();
    return;
  }
  flowStack.value = flowStack.value.slice(0, -1);
}

function onNavigate(contract) {
  const view = contract?.view || "";
  const payload = contract?.payload || {};
  navigate(view, payload);
}

function onCompleted(payload) {
  emit("completed", payload || {});
  flowContext.value = {
    ...flowContext.value,
    ...payload,
    routeStopId: "",
    stopId: "",
    completionMode: "",
  };
  flowStack.value = ["day-route"];
}

defineExpose({
  open,
  close,
});
</script>

<template>
  <div v-if="isOpen" class="mobile-flow-overlay">
    <section class="mobile-flow-shell" role="dialog" aria-modal="true" aria-label="Flujo mobile">
      <header class="mobile-flow-header modal-header">
        <h5 class="modal-title">App Móvil</h5>
        <button
          type="button"
          class="btn-close text-xxs"
          aria-label="Close"
          @click="close"
        ></button>
      </header>

      <div class="mobile-flow-body">
        <component
          :is="currentView"
          v-if="currentView"
          embedded
          :context="flowContext"
          @navigate="onNavigate"
          @back="back"
          @close="close"
          @completed="onCompleted"
        />
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.mobile-flow-overlay {
  align-items: center;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1060;
}

.mobile-flow-shell {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  box-shadow:
    0 28px 70px rgba(15, 23, 42, 0.16),
    0 8px 24px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  height: min(88vh, 820px);
  max-width: 420px;
  min-height: 680px;
  overflow: hidden;
  width: 100%;
}

.mobile-flow-header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid rgba(148, 163, 184, 0.24);
  display: flex;
  justify-content: space-between;
  padding: 0.8rem 0.95rem 0.7rem;
}

.modal-title {
  color: #344767;
  font-size: 0.96rem;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
}

.btn-close {
  background-position: center;
  background-size: 0.65rem;
  border-radius: 999px;
  box-shadow: none;
  height: 1.1rem;
  margin: 0;
  opacity: 0.7;
  padding: 0.22rem;
  transition:
    background-color 0.18s ease,
    opacity 0.18s ease,
    transform 0.18s ease;
  width: 1.1rem;
}

.btn-close:hover,
.btn-close:focus {
  background-color: rgba(148, 163, 184, 0.14);
  opacity: 1;
  transform: scale(1.02);
}

.mobile-flow-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  background: #0f1923 !important;

}
</style>
